import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { FullPageLoader } from '@/components/ui/full-page-loader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Calendar, ArrowLeft } from 'lucide-react';
import { formatPrice as formatPriceUtil } from "@/utils/currency";

export function BookingConfirmationPage() {
  const { storeUrl, bookingId } = useParams<{ storeUrl: string; bookingId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [booking, setBooking] = useState<any>(null);
  const [store, setStore] = useState<any>(null);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setIsLoading(true);
        
        // Fetch store
        const storeResponse = await api.get(`/api/public/stores/${storeUrl}`);
        setStore(storeResponse.data);
        
        // Fetch booking summary
        try {
          const bookingResponse = await api.get(`/api/bookings/${bookingId}/summary`);
          if (bookingResponse.data?.success) {
            setBooking(bookingResponse.data.booking);
          }
        } catch (bookingError) {
          // If summary fails, show basic confirmation
          console.error('Error fetching booking summary:', bookingError);
          setBooking({
            bookingNumber: bookingId?.slice(-8).toUpperCase() || 'CONFIRMED',
            status: 'pending',
          });
        }
      } catch (error) {
        console.error('Error fetching booking:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (storeUrl && bookingId) {
      fetchBooking();
    }
  }, [storeUrl, bookingId]);

  if (isLoading) {
    return <FullPageLoader text="Loading confirmation" variant="page" />;
  }

  const formatPrice = (price: number) =>
    formatPriceUtil(price, { currency: store?.business?.currency || 'NGN' });

  // Extract theme colors from store
  const themeColors = {
    primaryColor: store?.business?.settings?.colors?.primary || '#3b82f6',
    secondaryColor: store?.business?.settings?.colors?.secondary || '#10b981',
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Success Header */}
        <Card className="border border-gray-200 overflow-hidden">
          <div className="bg-green-50 border-b border-green-100 px-6 py-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle className="h-9 w-9 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Booking Confirmed!</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Your booking has been received and is being processed.
            </p>
          </div>

          {booking && (
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Booking Number</p>
                  <p className="font-bold text-xl mt-0.5">#{booking.bookingNumber || booking.id}</p>
                </div>
                {booking.status && (
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200 capitalize">
                    {booking.status}
                  </span>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {booking && (
          <>
            {/* Slot Details */}
            {booking.slot && (
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{booking.slot.name}</CardTitle>
                  {booking.slot.description && (
                    <p className="text-xs text-muted-foreground">{booking.slot.description}</p>
                  )}
                </CardHeader>
                <CardContent className="pt-0 space-y-0">
                  {booking.slot.bookingType && (
                    <div className="flex justify-between py-2 border-b border-gray-50 text-sm">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium capitalize">{booking.slot.bookingType.replace('_', ' ')}</span>
                    </div>
                  )}
                  {booking.slot.duration && (
                    <div className="flex justify-between py-2 border-b border-gray-50 text-sm">
                      <span className="text-muted-foreground">Duration</span>
                      <span className="font-medium">{booking.slot.duration}</span>
                    </div>
                  )}
                  {booking.slot.capacity && (
                    <div className="flex justify-between py-2 text-sm">
                      <span className="text-muted-foreground">Capacity</span>
                      <span className="font-medium">{booking.slot.capacity} {booking.slot.capacity === 1 ? 'person' : 'people'}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Schedule */}
            {booking.schedule && (
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Booking Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-0">
                  <div className="flex justify-between py-2 border-b border-gray-50 text-sm">
                    <span className="text-muted-foreground">Start</span>
                    <span className="font-medium">{booking.schedule.startDateTime || booking.schedule.startDate}</span>
                  </div>
                  <div className="flex justify-between py-2 text-sm">
                    <span className="text-muted-foreground">End</span>
                    <span className="font-medium">{booking.schedule.endDateTime || booking.schedule.endDate}</span>
                  </div>
                  {booking.schedule.quantity > 1 && (
                    <div className="flex justify-between py-2 text-sm">
                      <span className="text-muted-foreground">Quantity</span>
                      <span className="font-medium">{booking.schedule.quantity}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Customer Info */}
            {booking.customer && (
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Your Information</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-0">
                  <div className="flex justify-between py-2 border-b border-gray-50 text-sm">
                    <span className="text-muted-foreground">Name</span>
                    <span className="font-medium">{booking.customer.name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-50 text-sm">
                    <span className="text-muted-foreground">Email</span>
                    <span className="font-medium">{booking.customer.email}</span>
                  </div>
                  {booking.customer.phone && (
                    <div className="flex justify-between py-2 text-sm">
                      <span className="text-muted-foreground">Phone</span>
                      <span className="font-medium">{booking.customer.phone}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Pricing */}
            {booking.pricing && (
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Pricing Summary</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-0">
                  <div className="flex justify-between py-2 border-b border-gray-50 text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium">{formatPrice(booking.pricing.subtotal)}</span>
                  </div>
                  {booking.pricing.discount > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-50 text-sm text-green-600">
                      <span>Discount</span>
                      <span>−{formatPrice(booking.pricing.discount)}</span>
                    </div>
                  )}
                  {booking.pricing.tax > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-50 text-sm">
                      <span className="text-muted-foreground">Tax</span>
                      <span className="font-medium">{formatPrice(booking.pricing.tax)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 text-base font-semibold">
                    <span>Total</span>
                    <span>{formatPrice(booking.pricing.total)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment */}
            {booking.payment && (
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Payment</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-0">
                  <div className="flex justify-between py-2 border-b border-gray-50 text-sm">
                    <span className="text-muted-foreground">Method</span>
                    <span className="font-medium capitalize">{booking.payment.method.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between py-2 text-sm">
                    <span className="text-muted-foreground">Status</span>
                    <span className="font-medium capitalize">{booking.payment.status}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {booking.notes && (
              <Card className="border border-amber-200 bg-amber-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-amber-800">Your Notes</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground">{booking.notes}</p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Footer */}
        <div className="text-center space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">
            You'll receive a confirmation email with all the details shortly.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate(`/${storeUrl}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Store
          </Button>
        </div>
      </div>
    </div>
  );
}

